// Desk mini player: Chrome's Document Picture-in-Picture (Chrome and Edge 116+, on computers) opens a small window that
// stays on top of other apps and holds a page of its own. It shows the scene over a bar with play/pause, the city and
// the track, the previous and next city, the focus timer (start, pause, resume) and the sleep timer's time left.
// The window's page belongs to this one: its buttons call the app's own functions (toggle, step, timer), so the music
// keeps playing here and both show the same state (miniView, read again whenever the app calls refresh: the music, the
// city or a timer changed). Closing it (its own close button, back-to-tab, P or the dock button) leaves nothing behind.
// Where Document Picture-in-Picture is missing, the same button keeps the video one (site/js/engine/pip.js). This has
// its interface, so the render loop treats both alike: due(now, fps), then present(...) with the scenes' buffers. The
// scene is drawn once; present copies it 1:1 into the window's canvas, and CSS scales that to whole device pixels per
// scene pixel (fitScene, image-rendering: pixelated), the most that fit the window as it is resized.
// Chrome keeps requestAnimationFrame running for a page whose Picture-in-Picture window is open, in a background tab too.
import { W, H } from '../engine/core.js';
import { createPacer } from '../engine/display.js';
import { FOCUS_PRESETS, countdown, focusLeft } from './timers.js';

const FPS = 30;                                   // a calm background window doesn't need the full 60
const BAR = 44;                                   // CSS px: the control bar under the scene
export const MINI_PRESET = FOCUS_PRESETS[0];      // the focus timer its timer button starts

// Only a top-level page can open one: embedded in another site's frame, the video one still works.
export const miniPlayerSupported = () => typeof window !== 'undefined' && 'documentPictureInPicture' in window && window.top === window;

/**
 * Where the scene goes in a stage of `width` × `height` CSS px at `dpr` device px per CSS px: the most whole device
 * pixels per scene pixel that fit (`scale`), so every scene pixel is a crisp square, centred on whole device pixels.
 * A stage too small for even 1 shrinks it to fit. Sizes and offsets in CSS px.
 */
export function fitScene(width, height, dpr = 1) {
  const fit = Math.max(0, Math.min(width * dpr / W, height * dpr / H));
  const scale = fit >= 1 ? Math.floor(fit) : fit;
  const w = W * scale, h = H * scale;              // device px
  return {
    scale, width: w / dpr, height: h / dpr,
    left: Math.floor((width * dpr - w) / 2) / dpr, top: Math.floor((height * dpr - h) / 2) / dpr,
  };
}

/** What the bar shows for the app's state at `now` (ms): the music `playing`, the `city` and `track` names, and the
 *  timers.js `focus` and `sleep` timers (null when off). */
export function miniView({ playing, city, track, focus, sleep }, now) {
  const paused = focus?.left != null, left = focus ? countdown(focusLeft(focus, now)) : '';
  return {
    play: playing ? { icon: '#i-pause', label: 'Pause music' } : { icon: '#i-play', label: 'Play music' },
    city: city || 'Lofi Cities',
    track: track || 'Press play for endless lofi',
    timer: !focus ? { text: 'focus', on: false, label: `Start a ${MINI_PRESET.focus} minute focus timer` }
      : paused ? { text: `paused ${left}`, on: false, label: `Resume the focus timer, ${left} left` }
      : { text: `${focus.phase} ${left}`, on: true, label: `Pause the focus timer, ${focus.phase} ${left} left` },
    sleep: sleep ? `sleep ${countdown(sleep.endsAt - now)}` : '',
  };
}

// The window's own stylesheet: the dock's look (site/css/app.css #player), in a bar of its own under the scene.
const CSS = `
:root {
  --accent: #ffc86e; --text: #eceeff; --muted: #9ba0cc; --line: rgba(255, 255, 255, 0.09);
  --mono: ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, monospace;
  color-scheme: dark;
}
* { box-sizing: border-box; }
html, body {
  margin: 0; height: 100%; overflow: hidden; background: #05060f; color: var(--text);
  font: 13px/1.35 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; -webkit-font-smoothing: antialiased;
}
body { display: flex; flex-direction: column; user-select: none; }
.stage { position: relative; flex: 1; min-height: 0; }
.stage canvas { position: absolute; display: block; }
.bar {
  flex: none; height: ${BAR}px; display: flex; align-items: center; gap: 4px; padding: 0 8px 0 6px;
  border-top: 1px solid var(--line); background: rgba(10, 11, 28, 0.92);
}
button {
  flex: none; display: flex; align-items: center; justify-content: center; gap: 6px; height: 30px; min-width: 30px;
  padding: 0; border: 0; border-radius: 8px; background: transparent; font: inherit; color: inherit; cursor: pointer;
}
button:hover { background: rgba(255, 255, 255, .08); }
:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
button svg { flex: none; width: 14px; height: 14px; fill: currentColor; shape-rendering: crispEdges; }
.play { width: 32px; height: 32px; margin-right: 4px; border-radius: 10px; background: var(--accent); color: #12121c; }
.play:hover { background: var(--accent); filter: brightness(1.08); }
.now { flex: 1; min-width: 0; }
.city, .track { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.city { font-weight: 560; }
.track, .timer, .sleep { font: 11px var(--mono); color: var(--muted); }
.timer { padding: 0 8px; margin-left: 2px; }
.timer.on { color: var(--accent); }
.sleep { flex: none; padding-right: 2px; white-space: nowrap; }
`;
// The city arrows (8 × 8 like the page's own icons, site/index.html), beside the page's play, pause and timer.
const ARROWS = '<symbol id="mp-prev" viewBox="0 0 8 8"><path d="M4 1h2v1H4zM3 2h2v1H3zM2 3h2v2H2zM3 5h2v1H3zM4 6h2v1H4z"/></symbol>'
  + '<symbol id="mp-next" viewBox="0 0 8 8"><path d="M2 1h2v1H2zM3 2h2v1H3zM4 3h2v2H4zM3 5h2v1H3zM2 6h2v1H2z"/></symbol>';
const ICONS = ['i-play', 'i-pause', 'i-timer'];

/**
 * The mini player. `read()` returns the state miniView takes; `toggle()` plays or pauses the music, `step(±1)` goes to
 * the next or previous city and `timer()` starts, pauses or resumes the focus timer, each as the page's own controls do
 * (and the app calls refresh after). `onChange(open)` hears it open and close.
 */
export function createMiniPlayer({ onChange = () => {}, read, toggle, step, timer }) {
  let win = null, opening = false, canvas = null, ctx = null, el = null;
  let fitW = 0, fitH = 0, fitDpr = 0;
  const pace = createPacer(FPS);

  function build(w) {
    const doc = w.document;
    doc.title = 'Lofi Cities';
    const style = doc.createElement('style');
    style.textContent = CSS;
    doc.head.append(style);
    const icons = ICONS.map(id => document.getElementById(id)?.outerHTML ?? '').join('');
    doc.body.innerHTML = `<svg width="0" height="0" style="position:absolute" aria-hidden="true">${icons}${ARROWS}</svg>
      <div class="stage"><canvas width="${W}" height="${H}" role="img"></canvas></div>
      <div class="bar">
        <button class="play" type="button"><svg><use href="#i-play"/></svg></button>
        <div class="now"><span class="city"></span><span class="track"></span></div>
        <button class="prev" type="button" aria-label="Previous city" title="Previous city"><svg><use href="#mp-prev"/></svg></button>
        <button class="next" type="button" aria-label="Next city" title="Next city"><svg><use href="#mp-next"/></svg></button>
        <button class="timer" type="button"><svg><use href="#i-timer"/></svg><span></span></button>
        <span class="sleep" hidden></span>
      </div>`;
    const $ = sel => doc.querySelector(sel);
    canvas = $('canvas');
    ctx = canvas.getContext('2d', { alpha: false });
    ctx.imageSmoothingEnabled = false;
    el = { play: $('.play'), playIcon: $('.play use'), city: $('.city'), track: $('.track'), timer: $('.timer'), timerText: $('.timer span'), sleep: $('.sleep') };
    el.play.addEventListener('click', () => toggle());
    $('.prev').addEventListener('click', () => step(-1));
    $('.next').addEventListener('click', () => step(1));
    el.timer.addEventListener('click', () => timer());
    w.addEventListener('resize', layout);
    w.addEventListener('pagehide', () => {
      win = canvas = ctx = el = null;
      fitW = fitH = fitDpr = 0;
      onChange(false);
    }, { once: true });
  }

  /** the canvas at the most whole device pixels per scene pixel the window has room for (fitScene) */
  function layout() {
    if (!win) return;
    const w = win.innerWidth, h = Math.max(0, win.innerHeight - BAR), dpr = win.devicePixelRatio || 1;
    if (w === fitW && h === fitH && dpr === fitDpr) return;
    fitW = w; fitH = h; fitDpr = dpr;
    const f = fitScene(w, h, dpr), s = canvas.style;
    s.width = `${f.width}px`; s.height = `${f.height}px`; s.left = `${f.left}px`; s.top = `${f.top}px`;
    s.imageRendering = f.scale >= 1 ? 'pixelated' : 'auto';   // under 1x, nearest pixels would drop whole rows: smooth it like a thumbnail
  }

  const put = (node, text) => { if (node.textContent !== text) node.textContent = text; };
  function refresh() {
    if (!win) return;
    const v = miniView(read(), Date.now());
    el.playIcon.setAttribute('href', v.play.icon);
    el.play.setAttribute('aria-label', v.play.label);
    el.play.title = v.play.label;
    put(el.city, v.city);
    put(el.track, v.track);
    el.track.title = v.track;
    canvas.setAttribute('aria-label', `Animated pixel-art scene: ${v.city}`);
    put(el.timerText, v.timer.text);
    el.timer.classList.toggle('on', v.timer.on);
    el.timer.setAttribute('aria-label', v.timer.label);
    el.timer.title = v.timer.label;
    put(el.sleep, v.sleep);
    el.sleep.hidden = !v.sleep;
    // the city's accent (site/css/app.css --accent, set per scene on the page)
    win.document.documentElement.style.setProperty('--accent', getComputedStyle(document.documentElement).getPropertyValue('--accent'));
  }

  /** draw scene A, optionally cross-fading to scene B by `mix` (0..1) */
  function present(bufA, bufB = null, mix = 0) {
    if (!ctx) return;
    layout();                                       // a resize the window's event hasn't told yet, or a new screen's dpr
    ctx.globalAlpha = 1;
    ctx.drawImage(bufA, 0, 0);
    if (bufB && mix > 0) {
      ctx.globalAlpha = mix;
      ctx.drawImage(bufB, 0, 0);
      ctx.globalAlpha = 1;
    }
  }

  return {
    get active() { return Boolean(win); },
    /** opens the window showing `buf`; must start inside a click or key press (browsers require a gesture).
     *  Resolves true once it's open, false if it already was (or is still opening). */
    async open(buf) {
      if (win || opening) return false;
      opening = true;
      try {
        const w = await window.documentPictureInPicture.requestWindow({ width: W, height: H + BAR });
        win = w;
        build(w);
        layout();
        present(buf);
        refresh();
        onChange(true);
        return true;
      } finally {
        opening = false;
      }
    },
    close() { win?.close(); },
    /** the music paused or went on: the play button follows (the scene keeps moving, as on the page) */
    setPaused() { refresh(); },
    refresh,
    /** whether the render loop should present a frame at rAF time `now`: 30 a second (createPacer, which must see every
     *  frame while the window is open), `fps` where the screen gets fewer (Settings' Motion) */
    due(now, fps = FPS) {
      return Boolean(win) && pace(now, Math.min(FPS, fps));
    },
    present,
  };
}
